import _ from 'lodash';
import moment from 'moment';
import firstBy from 'thenby';
import { createActions, handleActions } from 'redux-actions';

import {
  getRequestsByFilter,
  getRequestsNextPage,
  getServiceConfigs,
  updateServiceRequest,
} from '../../api/ticket';

import appActions from '../../state/actions';
import { t } from '../../i18n';

import {
  getAffiliateLocations,
} from '../../api/gems';
import { CALL_REFRESHING_FUNC } from '../../state/types';

export const defaultState = {
  inLoad: false,
  inNextPageLoading: false,
  noSubscriptions: false,
  initialLoading: false,
  backgroundLoading: false,
  nextPageUrl: null,
  filter: 'open_resolved',
  sort: 'delivery',
  current: [],
  future: [],
  done: [],
  requests: [],
  pageNumber: 0,
  serviceConfigs: null,
  locations: null,
};

const requestActions = [
  'LOAD_START',
  'NO_SUBSCRIPTIONS',
  'LOAD_FINISH',
  'LOADED',
  'SET_FILTER',
  'SET_SORT',
  'LOAD_NEXT_PAGE',
  'LOADED_CONFIGS',
];

const getIdentityActions = list => list.reduce((map, actionName) =>
  ({ ...map, [actionName]: _.identity }), {});

const actionsNamespace = createActions({
  VIEWS: {
    REQUESTS: {
      ...getIdentityActions(requestActions),
    },
  },
});

const actions = actionsNamespace.views.requests;

export const reducer = handleActions({
  [actions.loadStart]: (state, { payload: isForeground }) =>
    ({ ...state, inLoad: true, initialLoading: isForeground, backgroundLoading: !isForeground }),
  [actions.noSubscriptions]: state => ({ ...state, noSubscriptions: true }),
  [actions.loadFinish]: state =>
    ({
      ...state,
      inLoad: false,
      initialLoading: false,
      backgroundLoading: false,
      inNextPageLoading: false,
    }),
  [actions.loaded]: (state, { payload: changes }) =>
    ({ ...state, ...changes }),
  [actions.setFilter]: (state, { payload: filter }) => ({ ...state, filter }),
  [actions.setSort]: (state, { payload: sort }) => ({ ...state, sort }),
  [actions.loadNextPage]: state => ({ ...state, inNextPageLoading: true }),
  [actions.loadedConfigs]: (state, { payload: changes }) => ({ ...state, ...changes }),
}, defaultState);

const severityOrder = {
  normal: 0,
  warning: 1,
  critical: 2,
};

const sortRequestsBySeverity = items =>
  items.sort(
    firstBy(({ request }) => request.severity_order, -1)
      .thenBy(({ request }) => moment(request.delivery).unix(), 1));

const sortRequestsByLocations = (locationsOrder, isAsc) =>
  (requests) => {
    const sorted = _.sortBy(requests, ({ request }) => locationsOrder[request.room]);

    if (!isAsc) {
      sorted.reverse();
    }

    return sorted;
  };

export const processResultItems = (result, append = false) =>
  (dispatch, getState) => {
    const {
      session: {
        channels: allChannels,
      },
      views: {
        requests: {
          serviceConfigs,
          locations,
          requests,
          sort,
          pageNumber,
        },
      },
    } = getState();

    const channels = allChannels.filter(({ type }) => type === 'service_request');
    const channelNames = _.keyBy(channels, 'name');
    const serviceConfigsByName = _.keyBy(serviceConfigs, 'service_name');
    const severitySorting = sort === 'severity';
    const isAscSort = sort[0] !== '-';
    const roomSorting = sort.replace(/^-/, '') === 'room';

    const locationsOrderIndex = locations.reduce((index, location, order) => {
      const { attributes: { name } } = location;
      return { ...index, [name]: order };
    }, {});

    const mapRequests = allRequests =>
      allRequests.map(request => ({
        request: { ...request, severity_order: severityOrder[request.severity] },
        channel: channelNames[request.channel],
        serviceConfig: serviceConfigsByName[request.type],
      }));

    const groupRequests = (items) => {
      const pointInFuture = moment().add(1, 'm');

      return _.groupBy(items, (({ request: { epochs, status } }) => {
        if (_.includes(['canceled', 'closed'], status)) {
          // Group all finished SR in done only for severity sorting
          return severitySorting ? 'done' : 'current';
        }

        const requestStart = _.find(epochs, { name: 'service_window_start' }).value;

        return pointInFuture.isBefore(requestStart) ? 'future' : 'current';
      }));
    };

    const sortRequests = (groupedRequests) => {
      const sortFunction = roomSorting ?
        sortRequestsByLocations(locationsOrderIndex, isAscSort) :
        sortRequestsBySeverity;

      const clientSorting = severitySorting || roomSorting;

      return clientSorting ?
        _.mapValues(groupedRequests, sortFunction) :
        groupedRequests;
    };

    const items = _.get(result, '_items');
    const allItems = append ? [
      ...requests,
      ...items,
    ] : items;

    const groups = _.flow([
      mapRequests,
      groupRequests,
      sortRequests,
    ])(allItems);

    dispatch(actions.loaded({
      current: [],
      future: [],
      done: [],
      ...groups,
      requests: allItems,
      pageNumber: append ? pageNumber + 1 : 0,
      nextPageUrl: _.get(result, '_links.next.href'),
    }));
  };

export const retrieveServiceRequestItems = (foreground = true) =>
  (dispatch, getState) => {
    const {
      // TODO: move to better place in state
      session: {
        affiliateId,
        userId,
        channels: allChannels,
        user: {
          attributes: { is_superuser: isAdmin },
        },
      },
      views: {
        requests: {
          filter,
          sort,
          current,
          future,
          done,
        },
      },
    } = getState();
    // Severity sorting on client
    const severitySorting = sort === 'severity';
    const roomSorting = sort.replace(/^-/, '') === 'room';
    const noContent = (current.length + future.length + done.length) === 0;
    const channels = allChannels.filter(({ type }) => type === 'service_request');

    dispatch(actions.loadStart(foreground || noContent));
    dispatch({
      type: CALL_REFRESHING_FUNC,
      payload: {
        key: 'Service Requests',
      },
    });

    if (!isAdmin && !channels.length) {
      return dispatch(actions.noSubscriptions());
    }

    return Promise.all([
      getServiceConfigs(affiliateId),
      roomSorting ? getAffiliateLocations(affiliateId) : [],
    ]).then(([serviceConfigs, locations]) => {
      dispatch(actions.loadedConfigs({ serviceConfigs, locations }));

      const requestFilter = {
        channels: _.map(channels, 'name'),
        status: filter,
        // for client sorting path default key
        sort: severitySorting ? 'delivery' : sort,
        assignedTo: userId,
        onlyMyChannels: !isAdmin,
      };

      return getRequestsByFilter(affiliateId, requestFilter)
        .then((result) => {
          dispatch(processResultItems(result));
        });
    })
      .then(() => dispatch(actions.loadFinish()))
      .catch(error => console.error(error));
  };

export const changeFilter = filter =>
  (dispatch) => {
    dispatch(actions.setFilter(filter));

    return dispatch(retrieveServiceRequestItems());
  };

export const changeSort = sort =>
  (dispatch) => {
    dispatch(actions.setSort(sort));

    return dispatch(retrieveServiceRequestItems(false));
  };

export const loadNextPage = nextPageUrl =>
  (dispatch) => {
    console.log('Next page loading', nextPageUrl);
    dispatch(actions.loadNextPage());

    getRequestsNextPage(nextPageUrl)
      .then((result) => {
        dispatch(processResultItems(result, true));
        dispatch(actions.loadFinish());
      });
  };

export const requestChangeStatus = (requestId, status, oldStatus) =>
  (dispatch, getState) => {
    const {
      session: {
        userId,
        user: {
          attributes,
        },
      },
    } = getState();

    const userFullName = [attributes.first_name, attributes.last_name]
      .filter(_.identity).join(' ');

    const payload = {
      acting_user: userId,
      acting_user_full_name: userFullName,
      status,
    };

    dispatch(actions.loadStart(false));

    const notificationPayload = {
      custom: requestChangeStatus,
      args: [requestId, oldStatus, false, { status }],
      actionLabel: t('Undo'),
      location: 'bottom-center',
      type: 'primary',
      sound: null,
    };

    return updateServiceRequest(requestId, payload)
      .then(() => (
          oldStatus ?
            dispatch(appActions.showNotification(t('Request was {status}', { status }), 5000, notificationPayload)) :
            null
      ))
      .then(() => dispatch(retrieveServiceRequestItems(false)));
  };



// WEBPACK FOOTER //
// ./src/views/requests/RequestState.js